import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useAuth } from '../../../contexts/AuthContext';
import { LeaveService } from '../../../services/leave';
import { LeaveBalanceService } from '../../../services/leaveBalanceService';
import { LeaveRequest } from '../../../types/leave';
import { UserRole } from '../../../types/user';
import { LeaveBalanceSnapshot } from '../../../types/leavePolicy';
import { LeaveBalanceDrawer } from '../LeaveBalanceDrawer';
import { expirySummary, expiryTooltip } from '../leaveExpiry';
import { getHolidaysForSelectedYear, getHolidaysForYear, Holiday } from '../../../data/holidays';
import { YearSelect, LEAVE_NAMES, LEAVE_COLORS, LEAVE_HISTORY_ANCHOR, balanceLevel } from './leaveShared';
import { sameIdentity } from '../../../services/identity';
import '../LeavePage.css';
import './LeaveDashboardV2.css';
import '../../common/emsPage.css';

const LEAVE_ICONS: Record<string, string> = {
  EL: '🌴',
  CL: '⚡',
  SL: '🏥',
  ML: '👶',
  CO: '⏱️',
};

const getDaysUntil = (dateStr: string): string => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(dateStr);
  target.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Tomorrow';
  if (diffDays > 1) return `In ${diffDays} days`;
  return 'Passed';
};

/**
 * The weekday a holiday falls on.
 *
 * Parsed as local midnight rather than via `new Date(iso)`, which reads the string as UTC and
 * can name the wrong day west of Greenwich — the same trap leaveDuration.parseISO documents.
 *
 * Shown only in the full-year dialog, where it answers the question that list is opened for:
 * a holiday landing on a Saturday is one nobody gains a day off from.
 */
const weekdayOf = (dateStr: string): string => {
  const [year, month, day] = dateStr.split('-').map(Number);
  if (!year || !month || !day) return '';
  return new Date(year, month - 1, day).toLocaleDateString(undefined, { weekday: 'long' });
};

/**
 * Every published holiday for one year, in a centred dialog.
 *
 * The card beside it lists only what is still to come, capped at five — which is the right
 * summary but leaves no way to see the rest of the calendar, or anything already passed. This is
 * a plain read-only list rather than an AppDialog: that component is an alertdialog built around
 * a tone badge, centred `pre-wrap` copy and an acknowledge button, none of which suit a
 * scrollable table of dates.
 *
 * Passed dates are dimmed rather than dropped — "the published calendar for 2026" that silently
 * omitted January would be a different thing from what the title claims.
 */
const HolidayListDialog: React.FC<{
  open: boolean;
  year: number;
  holidays: Holiday[];
  onClose: () => void;
}> = ({ open, year, holidays, onClose }) => {
  // Bound to the document, not the card: after a backdrop click focus sits outside the panel.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <div className="ld-hol-backdrop" onClick={onClose} role="presentation">
      <div
        className="ld-hol-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="ld-hol-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="ld-hol-head">
          <div>
            <h2 className="ld-hol-title" id="ld-hol-title">Holiday Calendar</h2>
            <p className="ld-hol-sub">
              {holidays.length} published {holidays.length === 1 ? 'holiday' : 'holidays'} for {year}
            </p>
          </div>
          <button type="button" className="ld-hol-close" onClick={onClose} aria-label="Close">✕</button>
        </header>

        <div className="ld-hol-body">
          {holidays.length === 0 ? (
            <p className="ld-v3-empty">No holidays published for {year} yet.</p>
          ) : (
            holidays.map((h) => {
              const countdown = getDaysUntil(h.date);
              const passed = countdown === 'Passed';
              return (
                <div key={h.date} className="ld-hol-row" data-passed={passed || undefined}>
                  <span className="ld-hol-date">{h.date}</span>
                  <span className="ld-hol-name">{h.name}</span>
                  <span className="ld-hol-weekday">{weekdayOf(h.date)}</span>
                  <span className="ld-hol-countdown">{countdown}</span>
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

const LeaveDashboardPage: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [leaves, setLeaves] = useState<LeaveRequest[]>([]);
  const [snapshots, setSnapshots] = useState<LeaveBalanceSnapshot[]>([]);
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedSnapshot, setSelectedSnapshot] = useState<LeaveBalanceSnapshot | null>(null);
  const [holidayDialogOpen, setHolidayDialogOpen] = useState(false);

  /**
   * Loading is two states, not one, because a first load and a background refresh should not
   * look the same.
   *
   * `loading` covers the cases where there is genuinely nothing to show — the first paint, and a
   * year change, which replaces every figure on the page — and renders skeletons. Before it
   * existed, both rendered the balance widget as an empty white card and the feed as "No leave
   * requests found", which is indistinguishable from an employee who has no leave: the page
   * looked finished and wrong rather than busy.
   *
   * `refreshing` covers the event-driven reloads (an approval elsewhere, a storage event). The
   * data on screen is still valid and is only about to be replaced, so it dims briefly instead of
   * collapsing to skeletons — the same distinction Leave Reports draws with .lr-refreshing.
   */
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  /** HR/Super Admin manage leave org-wide and don't need a personal balance box. */
  const isHRorSuperAdmin = user?.role === UserRole.HR_ADMIN || user?.role === UserRole.SUPER_ADMIN;
  /**
   * HR and Super Admin see every leave type; everyone else sees the ones they actually have.
   *
   * The rule lives in LeaveBalanceService.isBalanceVisibleToEmployee — it used to be a flat
   * "hide ML" here, which meant an employee HR had assigned Maternity or Paternity Leave to
   * never saw it: the days were in their balance, and this line dropped them on the way to
   * the screen.
   */
  const visibleSnapshots = useMemo(
    () => (isHRorSuperAdmin
      ? snapshots
      : snapshots.filter((s) => LeaveBalanceService.isBalanceVisibleToEmployee(s))),
    [snapshots, isHRorSuperAdmin]
  );

  /**
   * @param background true for an event-driven reload, which dims rather than skeletons.
   *
   * The two calls are sequential rather than a Promise.all on purpose: getBalances refreshes the
   * Comp-Off and adjustment caches that computeSnapshotsFor then reads synchronously, and it reads
   * the leave list through its own fetch. Racing them buys nothing here and would make the
   * finally-block ordering harder to reason about.
   */
  const loadData = useCallback(async (background = false) => {
    if (background) setRefreshing(true); else setLoading(true);
    try {
      const list = await LeaveService.listLeaves();
      setLeaves(list);
      if (user?.email) {
        setSnapshots(await LeaveBalanceService.getBalances(user.email, selectedYear));
      }
    } finally {
      // finally, not after the awaits: a failed fetch must still clear the skeletons, or the page
      // shimmers forever and reads as "still loading" when it has in fact given up.
      setLoading(false);
      setRefreshing(false);
    }
  }, [user?.email, selectedYear]);

  useEffect(() => {
    loadData();
    // A separate handler so the listeners take the background path — registering loadData itself
    // would pass the DOM Event as its first argument, which is truthy, so every foreground reload
    // would silently have been treated as a background one.
    const onExternalChange = () => { loadData(true); };
    window.addEventListener('leave-requests-updated', onExternalChange);
    window.addEventListener('leave-balance-updated', onExternalChange);
    window.addEventListener('leave-policy-updated', onExternalChange);
    window.addEventListener('storage', onExternalChange);
    return () => {
      window.removeEventListener('leave-requests-updated', onExternalChange);
      window.removeEventListener('leave-balance-updated', onExternalChange);
      window.removeEventListener('leave-policy-updated', onExternalChange);
      window.removeEventListener('storage', onExternalChange);
    };
  }, [loadData]);

  // Scoped to the selected year: switching to 2027 previously kept listing the
  // current year's requests, so the page silently mixed years.
  const ownRequests = useMemo(() => {
    if (!user?.email) return [];
    return leaves
      .filter((l) => sameIdentity(l.employeeEmail, user.email))
      .filter((l) => new Date(l.startDate).getFullYear() === selectedYear)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [leaves, user?.email, selectedYear]);

  const recentRequests = useMemo(() => ownRequests.slice(0, 5), [ownRequests]);

  // For the current year this is "still to come"; for a future year the whole
  // published calendar. Years with no published calendar yield an empty list
  // rather than falling back to another year's dates.
  const upcomingHolidays = useMemo(
    () => getHolidaysForSelectedYear(selectedYear, 5),
    [selectedYear]
  );

  /**
   * The whole published calendar for the year — what "View All" opens.
   *
   * getHolidaysForYear, not getHolidaysForSelectedYear: the card above deliberately hides what has
   * already passed, and a full-year list that started at today would be the same truncated view
   * under a name promising otherwise.
   */
  const allHolidays = useMemo(() => getHolidaysForYear(selectedYear), [selectedYear]);

  /*
   * HR and Super Admin see the WHOLE published calendar; everybody else sees what is still to
   * come.
   *
   * Not a cosmetic difference. "Upcoming" hides what has passed and stops at five, so from
   * autumn onwards an administrator opening this page reads "No holidays published for 2026
   * yet" against a calendar that is full — and they are the one person who would act on that,
   * by publishing dates that already exist. The list they maintain is the list they should be
   * shown.
   *
   * Both come from data/holidays, which resolves OrgConfigService's cache, so this is the same
   * calendar Organisation Setup writes rather than a second copy of it.
   */
  const holidayPanel = isHRorSuperAdmin ? allHolidays : upcomingHolidays;

  const handleViewDetails = (leaveTypeCode: string) => {
    const snap = snapshots.find((s) => s.leaveTypeCode.toUpperCase() === leaveTypeCode.toUpperCase());
    if (snap) {
      setSelectedSnapshot(snap);
      setDrawerOpen(true);
    }
  };

  const handleExport = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text('Leave Roster & Balances', 14, 18);
    doc.setFontSize(10);
    doc.text(`Generated on ${new Date().toLocaleDateString()} for Year ${selectedYear}`, 14, 25);
    autoTable(doc, {
      startY: 32,
      head: [['Leave Type', 'Allotment', 'Used', 'Pending', 'Available']],
      body: snapshots.map((s) => [
        LEAVE_NAMES[s.leaveTypeCode] || s.leaveTypeCode,
        // Includes days HR granted — see LeaveBalanceService.totalEntitlement.
        LeaveBalanceService.totalEntitlement(s),
        // totalUsed, not the bare `consumed` field: leave taken before the system went live
        // exists as an HR deduction rather than as a request, and the export printed 0 for it
        // beside a balance that visibly did not match the entitlement.
        LeaveBalanceService.totalUsed(s),
        s.pending,
        s.balance,
      ]),
    });
    doc.save(`leave_balances_${selectedYear}.pdf`);
  };

  return (
    <div className="ld-v3-wrapper">
      {/* 1. Header Section */}
      <motion.div
        className="ld-v3-header"
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <div>
          <h1 className="ld-v3-title">Leave Dashboard</h1>
          <p className="ld-v3-subtitle">Overview of your leave balance & time off for {selectedYear}</p>
        </div>

        <div className="ld-v3-actions">
          <YearSelect value={selectedYear} onChange={setSelectedYear} />
          {isHRorSuperAdmin && (
            <button type="button" className="ld-btn-subtle" onClick={() => navigate('/leave/reports')}>
              Reports
            </button>
          )}
          <button type="button" className="ld-btn-subtle" onClick={handleExport}>
            Export PDF
          </button>
          <button type="button" className="ld-btn-action" onClick={() => navigate('/leave/apply')}>
            + Apply Leave
          </button>
        </div>
      </motion.div>

      {/* 2. Single Unified Leave Balance Widget — HR/Super Admin manage leave org-wide, not their own balance */}
      {!isHRorSuperAdmin && (
      <motion.div
        className="ld-v3-widget"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.35, delay: 0.08 }}
      >
        {/* Compact Leave Breakdown List */}
        {/*
          Skeletons while the balances are being computed.
          Four rows because that is what a non-admin employee sees (EL, CL, SL, Comp-Off), so the
          card holds roughly its final height and the page does not jump when the real rows land.
          aria-busy and the visually-hidden status line carry the same message to a screen reader,
          which a shimmer alone says nothing to.
        */}
        {loading ? (
          <div className="ld-v3-policy-list" aria-busy="true">
            <span className="ld-v3-sr-only" role="status">Loading leave balances…</span>
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="ld-v3-policy-row" aria-hidden="true">
                <div className="ld-v3-policy-info">
                  <span className="ld-v3-skel ld-v3-skel--badge" />
                  <span className="ld-v3-policy-text">
                    <span className="ld-v3-skel ld-v3-skel--name" />
                  </span>
                </div>
                <div className="ld-v3-policy-bar-container">
                  <span className="ld-v3-skel ld-v3-skel--bar" />
                </div>
                <div className="ld-v3-policy-metrics">
                  <span className="ld-v3-skel ld-v3-skel--num" />
                  <span className="ld-v3-skel ld-v3-skel--link" />
                </div>
              </div>
            ))}
          </div>
        ) : (
        <div className={`ld-v3-policy-list${refreshing ? ' ld-v3-refreshing' : ''}`}>
          {visibleSnapshots.map((snap) => {
            const color = LEAVE_COLORS[snap.leaveTypeCode] || '#0d9488';
            const icon = LEAVE_ICONS[snap.leaveTypeCode] || '📄';
            const leaveName = LEAVE_NAMES[snap.leaveTypeCode] || snap.leaveTypeCode;
            const totalGranted = LeaveBalanceService.totalEntitlement(snap);
            /*
             * The real used figure, not the bare `consumed` field.
             *
             * The system went live mid-year, so leave taken before then was reconciled as a
             * negative HR adjustment rather than as a leave request — `consumed` cannot see
             * it. Reading it directly is what made this row say "granted 10 · used 0" beside
             * a balance of 7.5, three numbers that cannot all be true, and left the bar empty
             * for someone who had actually taken a quarter of their allowance.
             */
            const totalUsed = LeaveBalanceService.totalUsed(snap);
            // Shared with the service so the bar, the percentage and the numbers beside them
            // cannot disagree; safe at zero entitlement, which Comp-Off always is.
            const percent = LeaveBalanceService.utilisationPercent(snap);
            /*
             * The bar fills as leave is consumed, and its colour reports how much is left:
             * healthy → running low → gone. Two readings of the same fact, which is why they
             * are separate encodings — length answers "how much have I taken", colour answers
             * "should I be worried".
             *
             * Shared thresholds with Leave Policies (balanceLevel), so a balance called "low"
             * on one screen is not called healthy on the other. Entitlement rather than the
             * bare granted field, or Comp-Off — earned, never granted — divides by zero.
             *
             * Colour is never the only cue: the day count sits beside the bar and the title
             * spells the same thing out, so this stays readable without colour vision.
             */
            const level = balanceLevel(snap.balance, totalGranted);
            // Soonest first from the service, so the head of the list is the next deadline.
            const nextExpiry = snap.expiringGrants?.[0];

            return (
              <div key={snap.id} className="ld-v3-policy-row">
                <div className="ld-v3-policy-info">
                  {/* Icon only. The full leave name is the sibling below, so the badge
                      previously printed the raw code purely as a duplicate identifier. */}
                  <span className="ld-v3-policy-badge" style={{ backgroundColor: `${color}18`, color }}>
                    {icon}
                  </span>
                  {/*
                    Name and chip stack, rather than sitting in one row beside the badge.
                    This column is a fixed 220px in the row grid; the chip is ~140px and does
                    not wrap, which left about 30px for the name — so "Comp-Off" broke at its
                    hyphen and the chip overflowed past the card edge. Stacking gives each its
                    own line and the chip the full column width.
                  */}
                  <span className="ld-v3-policy-text">
                    <span className="ld-v3-policy-name">{leaveName}</span>
                    {/* The balance drops these on the expiry date whether or not anyone was
                        told. Warning beforehand is the point of storing the date at all. */}
                    {/* The chip states the date, not only a countdown. "expiring in 6 days"
                        cannot be acted on without doing the arithmetic, and the actual date
                        was reachable only by hovering — invisible on touch entirely. The
                        full per-date breakdown stays in the tooltip for the multi-date case. */}
                    {nextExpiry && (
                      <span
                        className="ld-v3-expiry-chip"
                        title={expiryTooltip(snap.expiringGrants)}
                      >
                        ⏳ {expirySummary(snap.expiringGrants)}
                      </span>
                    )}
                  </span>
                </div>

                <div className="ld-v3-policy-bar-container">
                  {/* data-level rather than an inline backgroundColor: the three severity
                      colours each need a dark-theme counterpart, and a colour computed in JS
                      cannot follow the theme toggle without wiring a theme hook in here. The
                      leave type's own colour still identifies the row via the badge above. */}
                  {/* The four figures stay in the title and in the Details drawer rather than
                      printed on the row: spelled out inline they crowded a row that is mostly
                      a bar, and the balance beside it is what the row is actually for. */}
                  <div
                    className="ld-v3-bar-bg"
                    title={`${totalUsed} of ${totalGranted} day(s) used · ${snap.pending} pending · ${snap.balance} left`}
                  >
                    <div className="ld-v3-bar-fill" data-level={level} style={{ width: `${percent}%` }} />
                  </div>
                </div>

                <div className="ld-v3-policy-metrics">
                  <span className="ld-v3-bal-num" style={snap.balance < 0 ? { color: '#ef4444' } : undefined}>
                    {snap.balance} <span className="ld-v3-bal-sub">/ {totalGranted} left</span>
                  </span>
                  <button type="button" className="ld-v3-link" onClick={() => handleViewDetails(snap.leaveTypeCode)}>
                    Details →
                  </button>
                </div>
              </div>
            );
          })}

          {/* Not the same thing as loading, and it used to be indistinguishable from it: an empty
              card said nothing at all. Reached when the directory has no Date of Joining for this
              employee, which zeroes every snapshot — see computeSnapshotsFor's dojMissing. */}
          {visibleSnapshots.length === 0 && (
            <p className="ld-v3-empty">No leave balances available for {selectedYear}.</p>
          )}
        </div>
        )}
      </motion.div>
      )}

      {/* Leave Balance Drawer */}
      <LeaveBalanceDrawer
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        snapshot={selectedSnapshot}
        leaveName={selectedSnapshot ? (LEAVE_NAMES[selectedSnapshot.leaveTypeCode] || selectedSnapshot.leaveTypeCode) : ''}
        historyRequests={leaves.filter((l) => sameIdentity(l.employeeEmail, user?.email))}
        year={selectedYear}
      />

      {/* 3. Bottom 2-Column Split: Activity Feed + Holiday Timeline */}
      <motion.div
        className="ld-v3-bottom-grid"
        initial={{ opacity: 0, y: 15 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: 0.15 }}
      >
        {/* Left: Compact Activity Feed */}
        <div className="ld-v3-section">
          <div className="ld-v3-section-head">
            <h3>Recent Leave Requests</h3>
            {/* The card's only "View All" — there used to be a second, identical one below the
                list, so a truncated feed showed the same link twice.
              *
              * Shown only when the feed is actually truncated: offering "View All" while every
              * request is already on screen promises more than the link can deliver.
              *
              * Everyone's own leave history lives on Apply Leave, regardless of role — Reports
              * is HR/Super Admin-only and used to bounce Managers to /dashboard (Expenses).
              * That page opens on the month calendar, so the hash scrolls to the history table
              * rather than dropping the employee at the top of a calendar they didn't ask for. */}
            {/* Hidden while loading: the count it is gated on is 0 until the fetch lands, so the
                link would pop in a beat after the list. */}
            {!loading && ownRequests.length > recentRequests.length && (
              <button
                type="button"
                className="ld-v3-link"
                onClick={() => navigate(`/leave/apply#${LEAVE_HISTORY_ANCHOR}`)}
              >
                View All →
              </button>
            )}
          </div>

          {/* The skeleton this feed most needed: "No leave requests found for 2026" is a real,
              plausible answer, so rendering it during the fetch stated something false rather
              than merely looking unfinished. */}
          {loading ? (
            <div className="ld-v3-feed" aria-busy="true">
              <span className="ld-v3-sr-only" role="status">Loading leave requests…</span>
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="ld-v3-feed-item" aria-hidden="true">
                  <span className="ld-v3-skel ld-v3-skel--feed-icon" />
                  <div className="ld-v3-feed-details">
                    <div className="ld-v3-feed-top">
                      <span className="ld-v3-skel ld-v3-skel--feed-title" />
                      <span className="ld-v3-skel ld-v3-skel--feed-pill" />
                    </div>
                    <span className="ld-v3-skel ld-v3-skel--feed-sub" />
                  </div>
                </div>
              ))}
            </div>
          ) : recentRequests.length === 0 ? (
            <p className="ld-v3-empty">No leave requests found for {selectedYear}.</p>
          ) : (
            <div className={`ld-v3-feed${refreshing ? ' ld-v3-refreshing' : ''}`}>
              {recentRequests.map((req) => {
                const color = LEAVE_COLORS[req.type] || '#0d9488';
                const icon = LEAVE_ICONS[req.type] || '📄';
                const statusLower = String(req.status).toLowerCase();
                return (
                  <div key={req.id} className="ld-v3-feed-item">
                    <div className="ld-v3-feed-icon" style={{ background: `${color}14`, color }}>
                      {icon}
                    </div>
                    <div className="ld-v3-feed-details">
                      <div className="ld-v3-feed-top">
                        <span className="ld-v3-feed-title">{LEAVE_NAMES[req.type] || req.type} Request</span>
                        <span className={`ld-v3-pill ld-v3-pill--${statusLower}`}>{req.status}</span>
                      </div>
                      <div className="ld-v3-feed-sub">
                        <span>📅 {req.startDate} to {req.endDate}</span>
                        {req.reason && <span className="ld-v3-feed-reason"> • "{req.reason}"</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* The duplicate bottom "View All" lived here; the card head owns the link now. */}
        </div>

        {/* Right: Clean Holiday Timeline */}
        <div className="ld-v3-section">
          <div className="ld-v3-section-head">
            {/* Named for what it actually lists, so the heading cannot promise the wrong thing. */}
            <h3>{isHRorSuperAdmin ? 'Public Holidays' : 'Upcoming Holidays'}</h3>
            {/* The year pill keeps its place; the link sits beside it, matching where Recent
                Leave Requests puts its own View All. Shown only when the dialog would actually
                add something — with every published holiday already listed, the link promises
                more than it can deliver, which is the rule that card's link already follows. */}
            <div className="ld-v3-head-right">
              <span className="ld-v3-year-pill">{selectedYear}</span>
              {/* An administrator is already looking at every one of them, so View All would
                  open a dialog showing what is on screen. They get the way to CHANGE the list
                  instead — the question the full calendar actually raises. */}
              {isHRorSuperAdmin ? (
                <button
                  type="button"
                  className="ld-v3-link"
                  onClick={() => navigate('/settings?tab=org-lists')}
                  title="Maintained in Settings → Organisation Setup"
                >
                  Manage →
                </button>
              ) : (
                allHolidays.length > upcomingHolidays.length && (
                  <button
                    type="button"
                    className="ld-v3-link"
                    onClick={() => setHolidayDialogOpen(true)}
                  >
                    View All →
                  </button>
                )
              )}
            </div>
          </div>

          {holidayPanel.length === 0 ? (
            <p className="ld-v3-empty">
              {isHRorSuperAdmin
                ? `No holidays published for ${selectedYear}. Add them in Organisation Setup.`
                : `No holidays published for ${selectedYear} yet.`}
            </p>
          ) : (
            /* Capped in height for the full-year list, so a twelve-date calendar does not
               stretch the row and leave the requests card beside it short. */
            <div className={`ld-v3-timeline${isHRorSuperAdmin ? ' ld-v3-timeline--full' : ''}`}>
              {holidayPanel.map((h) => (
                <div key={h.date} className="ld-v3-t-item">
                  <div className="ld-v3-t-date">
                    <span>{h.date}</span>
                  </div>
                  <div className="ld-v3-t-content">
                    <span className="ld-v3-t-name">{h.name}</span>
                    <span className="ld-v3-t-countdown">{getDaysUntil(h.date)}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </motion.div>

      <HolidayListDialog
        open={holidayDialogOpen}
        year={selectedYear}
        holidays={allHolidays}
        onClose={() => setHolidayDialogOpen(false)}
      />
    </div>
  );
};

export default LeaveDashboardPage;
